import { useState, useEffect } from "react";
import axios from "axios";
import {
  Monitor, Plus, Edit3, Trash2, Loader2, RefreshCw,
  CheckCircle2, AlertTriangle, Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestServiceITInventory() {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [allocDialog, setAllocDialog] = useState({ open: false, itemId: null });

  const [form, setForm] = useState({
    item_name: "",
    category: "",
    serial_number: "",
    status: "available",
    assigned_to: "",
    notes: "",
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/request/inventory/it`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch (err) {
      toast.error("Failed to load IT inventory");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm({ item_name: "", category: "", serial_number: "", status: "available", assigned_to: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      item_name: item.item_name || "",
      category: item.category || "",
      serial_number: item.serial_number || "",
      status: item.status || "available",
      assigned_to: item.assigned_to || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editItem) {
        await axios.put(`${API}/request/inventory/it/${editItem.id}`, form, { headers });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/request/inventory/it`, form, { headers });
        toast.success("Item created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/request/inventory/it/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item deleted");
      fetchItems();
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  const handleAllocate = async () => {
    if (!allocDialog.itemId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/inventory/it/${allocDialog.itemId}/allocate`,
        { assigned_to: allocDialog.assigned_to || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Item allocated");
      setAllocDialog({ open: false, itemId: null, assigned_to: "" });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to allocate");
    }
  };

  const countByStatus = (status) => items.filter((i) => i.status === status).length;
  const lowStock = items.filter((i) => i.status === "broken" || i.status === "disposed");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          IT Inventory
        </h1>
        <p className="text-slate-500 mt-1">Manage hardware, software and IT assets</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-800">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500">Available</p>
          <p className="text-2xl font-bold text-emerald-600">{countByStatus("available")}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500">Allocated</p>
          <p className="text-2xl font-bold text-blue-600">{countByStatus("allocated")}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500">Broken/Disposed</p>
          <p className="text-2xl font-bold text-red-600">{lowStock.length}</p>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-700">
            {lowStock.length} item(s) need attention: {lowStock.map((i) => i.item_name).join(", ")}
          </p>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-4">
        <Button onClick={openCreate} className="bg-teal-500 hover:bg-teal-600">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
        <Button variant="outline" onClick={fetchItems}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-app">
              <th className="text-left p-4 font-semibold text-slate-600">Item</th>
              <th className="text-left p-4 font-semibold text-slate-600">Category</th>
              <th className="text-left p-4 font-semibold text-slate-600">Serial #</th>
              <th className="text-left p-4 font-semibold text-slate-600">Status</th>
              <th className="text-left p-4 font-semibold text-slate-600">Assigned To</th>
              <th className="text-left p-4 font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">
                  No inventory items
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-4 font-medium text-slate-800">{item.item_name}</td>
                  <td className="p-4 text-slate-600">{item.category || "-"}</td>
                  <td className="p-4 text-slate-500 font-mono text-xs">{item.serial_number || "-"}</td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.status === "available"
                          ? "bg-emerald-100 text-emerald-700"
                          : item.status === "allocated"
                          ? "bg-blue-100 text-blue-700"
                          : item.status === "broken"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{item.assigned_to || "-"}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {item.status === "available" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAllocDialog({ open: true, itemId: item.id, assigned_to: "" })}
                        >
                          <CheckCircle2 className="w-4 h-4 text-blue-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                        <Edit3 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Item" : "Add Item"}</DialogTitle>
            <DialogDescription>
              {editItem ? "Update inventory item details" : "Add a new item to inventory"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Item Name *</label>
              <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Serial Number</label>
              <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["available", "allocated", "broken", "disposed"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button onClick={handleSave} className="bg-teal-500 hover:bg-teal-600 w-full">
              {editItem ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog
        open={allocDialog.open}
        onOpenChange={(o) => setAllocDialog({ ...allocDialog, open: o })}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Allocate Item</DialogTitle>
            <DialogDescription>Assign this item to a user</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="User ID or name"
              value={allocDialog.assigned_to || ""}
              onChange={(e) => setAllocDialog({ ...allocDialog, assigned_to: e.target.value })}
            />
            <Button onClick={handleAllocate} className="bg-teal-500 hover:bg-teal-600 w-full">
              Allocate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
